import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  advancement as advApi,
  ApiError,
  dens as densApi,
  finance as financeApi,
  scouts as scoutsApi,
  type DenRow,
  type ScoutRow,
  type ScoutSummary,
} from '../api';
import { useUnits } from '../units';

export function Roster() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole, councilCanView } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('DEN_LEADER') || councilCanView('roster');
  const isAdmin = hasRole('ADMIN');

  const [dens, setDens] = useState<DenRow[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>(''); // '' = all, denId, or '__none__'
  const [rows, setRows] = useState<ScoutRow[] | null>(null);
  const [ready, setReady] = useState<ScoutSummary[]>([]);
  const [paid, setPaid] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    const params =
      filter === '__none__' ? { unassigned: true } : filter ? { denId: filter } : undefined;
    scoutsApi
      .list(unitId, params)
      .then((r) => {
        setRows(r.scouts);
        setYear(r.programYear);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load roster'));
  }, [unitId, filter]);

  useEffect(() => {
    if (!mayView) return;
    densApi.list(unitId).then((r) => setDens(r.dens)).catch(() => setDens([]));
    advApi
      .summaries(unitId)
      .then((s) => setReady(s.filter((x) => x.readyToAward)))
      .catch(() => setReady([]));
    financeApi
      .paidStatus(unitId)
      .then((s) => setPaid(Object.fromEntries(s.map((x) => [x.scoutId, x.paid]))))
      .catch(() => setPaid({}));
  }, [unitId, mayView]);

  useEffect(() => {
    if (mayView) load();
  }, [mayView, load]);

  if (!mayView) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Roster</h1>
        <Link to={`/units/${unitId}/roster/import`} className="tile-link">
          Import from CSV →
        </Link>
      </div>
      <p className="muted">
        {currentUnit?.name} · program year {year ?? '…'}
      </p>
      {error && <p className="error">{error}</p>}

      {ready.length > 0 && (
        <div className="callout ok">
          🎖️ Ready to award:{' '}
          {ready.map((s, i) => (
            <span key={s.scoutId}>
              {i > 0 && ', '}
              <Link to={`/units/${unitId}/scouts/${s.scoutId}`}>
                {s.firstName} {s.lastName}
              </Link>{' '}
              ({s.rankLabel})
            </span>
          ))}
        </div>
      )}

      <AddScoutForm unitId={unitId} dens={dens} denRequired={!isAdmin} onAdded={load} />

      <label className="filter">
        Show:{' '}
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All dens</option>
          {dens.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
          {isAdmin && <option value="__none__">Unassigned</option>}
        </select>
      </label>

      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Grade</th>
            <th>Den</th>
            <th>Dues</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows?.map((s) => (
            <ScoutRowView
              key={s.id}
              unitId={unitId}
              scout={s}
              dens={dens}
              paid={paid[s.id]}
              onChanged={load}
            />
          ))}
          {rows?.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                No Scouts{filter ? ' match this filter' : ' yet'}.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AddScoutForm({
  unitId,
  dens,
  denRequired,
  onAdded,
}: {
  unitId: string;
  dens: DenRow[];
  denRequired: boolean;
  onAdded: () => void;
}) {
  const [firstName, setFirst] = useState('');
  const [lastName, setLast] = useState('');
  const [grade, setGrade] = useState('');
  const [denId, setDenId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await scoutsApi.create(unitId, {
        firstName,
        lastName,
        grade: grade || undefined,
        denId: denId || undefined,
      });
      setFirst('');
      setLast('');
      setGrade('');
      setDenId('');
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add Scout');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={submit}>
      <input placeholder="First name" value={firstName} onChange={(e) => setFirst(e.target.value)} required />
      <input placeholder="Last name" value={lastName} onChange={(e) => setLast(e.target.value)} required />
      <input
        placeholder="Grade"
        value={grade}
        onChange={(e) => setGrade(e.target.value)}
        style={{ minWidth: 70 }}
      />
      <select value={denId} onChange={(e) => setDenId(e.target.value)} required={denRequired}>
        <option value="">{denRequired ? 'Choose den…' : 'No den yet'}</option>
        {dens.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      <button type="submit" className="primary" disabled={busy}>
        {busy ? '…' : 'Add Scout'}
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}

function ScoutRowView({
  unitId,
  scout,
  dens,
  paid,
  onChanged,
}: {
  unitId: string;
  scout: ScoutRow;
  dens: DenRow[];
  paid: boolean | undefined;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reassign(denId: string) {
    setError(null);
    setBusy(true);
    try {
      await scoutsApi.assignDen(unitId, scout.id, denId || null);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not move Scout');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Remove ${scout.firstName} ${scout.lastName} from the roster?`)) return;
    setBusy(true);
    try {
      await scoutsApi.remove(unitId, scout.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove');
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        <Link to={`/units/${unitId}/scouts/${scout.id}`}>
          {scout.lastName}, {scout.firstName}
        </Link>
      </td>
      <td>{scout.grade ?? '—'}</td>
      <td>
        <select
          value={scout.den?.id ?? ''}
          disabled={busy}
          onChange={(e) => reassign(e.target.value)}
        >
          <option value="">— unassigned —</option>
          {dens.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        {error && <div className="error">{error}</div>}
      </td>
      <td>
        {paid === undefined ? (
          <span className="muted">—</span>
        ) : paid ? (
          <span className="muted small">paid</span>
        ) : (
          <span className="badge">owes</span>
        )}
      </td>
      <td className="row-actions">
        <button onClick={remove} disabled={busy}>
          Remove
        </button>
      </td>
    </tr>
  );
}
